import React from 'react';
import { API_BASE_URL } from '../../config';
import './TriggerAlert.css';

export interface TriggerAlertData {
  id: number;
  trigger_type: string;
  trigger_data: any;
  created_at: string;
}

interface TriggerAlertProps {
  alert: TriggerAlertData;
  onAdjust: () => void;
  onDismiss: () => void;
}

const WarningIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

function getTriggerMessage(type: string, data: any): string {
  switch (type) {
    case 'rpe_mismatch':
      return `Your RPE was ${data?.rpe ?? '?'} on ${data?.activity_name ?? 'a ride'}, but intensity was low (IF ${data?.intensity_factor != null ? data.intensity_factor.toFixed(2) : '?'}). This could indicate fatigue or illness.`;
    case 'cumulative_fatigue':
      return `You've rated ${data?.consecutive_count ?? 'several'} consecutive rides as RPE 7+. Accumulated fatigue may be building.`;
    case 'hrv_crash':
      return `Your HRV dropped significantly below your 7-day average (${data?.current_hrv ?? '?'} vs avg ${data?.avg_hrv_7d ?? '?'}).`;
    case 'sleep_deprivation':
      return `You've had less than 6 hours of sleep for ${data?.consecutive_days ?? '?'}+ consecutive days.`;
    default:
      return `An adaptive coaching alert was triggered: ${type}.`;
  }
}

const TriggerAlert: React.FC<TriggerAlertProps> = ({ alert, onAdjust, onDismiss }) => {
  const message = getTriggerMessage(alert.trigger_type, alert.trigger_data);

  const handleDismiss = async () => {
    try {
      await fetch(`${API_BASE_URL}/api/ai-coach/trigger-alerts/${alert.id}/dismiss`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (e) {
      console.error('Failed to dismiss trigger alert:', e);
    }
    onDismiss();
  };

  return (
    <div className="trigger-alert">
      <div className="trigger-alert-icon">
        <WarningIcon />
      </div>
      <div className="trigger-alert-content">
        <div className="trigger-alert-message">{message}</div>
        <div className="trigger-alert-actions">
          <button className="trigger-alert-adjust" onClick={onAdjust}>
            Adjust plan
          </button>
          <button className="trigger-alert-dismiss" onClick={handleDismiss}>
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
};

export default TriggerAlert;
